import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, CheckBox, FormField, Heading, ResponsiveContext, Text } from "grommet";
import * as React from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission } from "../account/AccountStore";
import AutoSizeTextArea from "../components/AutoSizeTextArea";
import CustomButton from "../components/CustomButton";
import { NavigationButton } from "../components/NavigationButton";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import { SeasonRow } from "../seasons/SeasonRow";
import { getTeams, TeamViewModel } from "../teams/TeamsStore";
import LeagueDetails from "./LeagueDetails";
import { getLeague, LeagueViewModel, updateLeague } from "./LeaguesStore";
import DateScrollView from "../components/DateScrollView";
import LeagueTable from "./LeagueTable";
import { getStats } from "../stats/StatsStore";

export const newSeasonIcon = (
    <span className="fa-layers fa-fw">
        {/* @ts-ignore */}
        <FontAwesomeIcon size="lg" icon={["fad", "calendar-range"]}></FontAwesomeIcon>
        <FontAwesomeIcon icon={["fad", "plus"]} swapOpacity transform="up-12 right-12" />
    </span>
);
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const chain = <FontAwesomeIcon icon={["fad", "chain"]}></FontAwesomeIcon>;
const xmark = <FontAwesomeIcon icon={["fad", "xmark"]}></FontAwesomeIcon>;
// @ts-ignore
const archive = <FontAwesomeIcon size="xl" icon={["fad", "archive"]}></FontAwesomeIcon>;

const LeagueProfile = () => {
    let { leagueID } = useParams<{ leagueID: string }>();

    let { pathname } = useLocation();
    let navigate = useNavigate();

    const [league, setLeague] = React.useState(new LeagueViewModel());
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
        if (league.id) {
            if (pathname == `/leagues/${leagueID}`) {
                navigate(`/leagues/${leagueID}/seasons`, { replace: true });
            }
        }
    }, [pathname, league.id]);

    const [rules, setRules] = React.useState(league.rules || "");
    const [rulesAsMarkdown, setRulesAsMarkdown] = React.useState(league.rulesAsMarkdown || false);

    let [error, setError] = React.useState("");

    const [showArchive, setShowArchive] = React.useState(false);

    React.useEffect(() => {
        loadLeague();
    }, [showArchive]);

    const size = React.useContext(ResponsiveContext);

    const loadLeague = () => {
        setIsLoading(true);
        getLeague(leagueID, showArchive).then((league) => {
            setLeague(league);
            setIsLoading(false);
            setRules(league.rules);
            setRulesAsMarkdown(league.rulesAsMarkdown);
        });
    };

    // const [upcomingMatches, setUpcomingMatches] = React.useState([] as LeagueMatchViewModel[]);
    // const [pastMatches, setPastMatches] = React.useState([] as LeagueMatchViewModel[]);

    const [isUpdating, setIsUpdating] = React.useState(false);

    const onChange = (updatedLeague) => {
        setLeague({ ...league, ...updatedLeague });
    };

    let update = () => {
        setIsUpdating(true);
        updateLeague({ ...league, rules, rulesAsMarkdown } as LeagueViewModel).then(
            (result) => {
                getLeague(leagueID).then((league) => setLeague(league));
                setIsUpdating(false);
            },
            (error) => {
                setError(error);
                setIsUpdating(false);
            }
        );
    };

    const [teams, setTeams] = React.useState([] as TeamViewModel[]);
    const [statTypes, setStatTypes] = React.useState([] as StatViewModel[]);

    React.useEffect(() => {
        getTeams("").then((teams) => {
            setTeams(teams);
            setTeamSearchResult(teams);
        });

        getStats().then((result) => setStatTypes(result.result));
    }, []);

    let [participants, setParticipants] = React.useState([]);

    let [teamSearchResult, setTeamSearchResult] = React.useState([] as Array<TeamViewModel>);

    const search = (filter) => {
        setTeamSearchResult(
            teams.filter((r) => {
                return r.name.toLowerCase().includes(filter.toLowerCase());
            })
        );
    };

    let [teamFilter, setTeamFilter] = React.useState("");
    const [role, setRole] = React.useState("");

    React.useEffect(() => {
        search(teamFilter);
    }, [teamFilter]);

    // const [darkMode, setDarkMode] = React.useState(true);
    // const [useTeamColor, setUseTeamColor] = React.useState(true);
    // const [reverse, setReverse] = React.useState(false);
    // const [backgroundColor, setBackgroundColor] = React.useState("transparent");
    // const [matchNumber, setMatchNumber] = React.useState(0);

    const hasWritePermission = usePermission("sport:leagues.write");
    const hasSeasonCreatePermissions = usePermission("sport:seasons.write");
    const [showArchiveModal, setShowArchiveModal] = React.useState(false);

    const startArchive = () => {
        setShowArchiveModal(true);
    };

    const toggleArchive = () => {
        updateLeague({ ...league, archived: !league.archived } as LeagueViewModel).then(() => {
            loadLeague();
            setShowArchiveModal(false);
        });
    };

    return (
        <Box>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    <Box direction="row" align="center" gap="small">
                        {league.name}
                    </Box>
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium" wrap>
                    {/* <NavigationButton href={`/leagues/${league.id}/result`} text={"Result"}></NavigationButton> */}
                    <NavigationButton href={`/leagues/${league.id}/seasons`} text={"Seasons"}></NavigationButton>
                    <NavigationButton href={`/leagues/${league.id}/details`} text={"Details"}></NavigationButton>
                    <NavigationButton href={`/leagues/${league.id}/rules`} text={"Rules"}></NavigationButton>
                    {league.isPublic && <NavigationButton href={`/leagues/${league.id}/public`} text={"Public Page"}></NavigationButton>}
                    {error && <NavigationButton href={`/leagues/${league.id}/error`} text={"Errors"}></NavigationButton>}
                </Box>
                <br />
                <Routes>
                    {/* <Route
                        path="result"
                        element={
                            <>
                                <Box gap="small">
                                    {upcomingMatches.length > 0 && (
                                        <>
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Upcoming Matches
                                            </Text>
                                            <Box gap="small">
                                                {upcomingMatches.map((match) => (
                                                    <Stack key={match.leagueMatchID} anchor="top-left">
                                                        <MatchRow match={match.match}></MatchRow>
                                                        <Box pad={"small"}>
                                                            <Text size="small">{match.leagueMatchID}</Text>
                                                        </Box>
                                                    </Stack>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                    {pastMatches.length > 0 && (
                                        <>
                                            <Text weight={"bold"} style={{ textTransform: "uppercase" }}>
                                                Latest Matches
                                            </Text>
                                            <Box gap="small">
                                                {pastMatches.map((match) => (
                                                    <Stack key={match.leagueMatchID} anchor="top-left">
                                                        <MatchRow match={match.match}></MatchRow>
                                                        <Box pad={"small"}>
                                                            <Text size="small">{match.leagueMatchID}</Text>
                                                        </Box>
                                                    </Stack>
                                                ))}
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </>
                        }
                    /> */}
                    <Route
                        path="seasons"
                        element={
                            <>
                                <Box direction="row" pad={"small"} gap="small">
                                    {hasSeasonCreatePermissions && <CustomButton path={`/seasons/create/${league.id}`} icon={newSeasonIcon} label="New Season"></CustomButton>}
                                    <CustomButton icon={archive} primary={showArchive} label={showArchive ? "Show Active" : "Show Archive"} onClick={() => setShowArchive(!showArchive)}></CustomButton>
                                </Box>
                                <Box gap="small">
                                    <DateScrollView data={league?.seasons || []} render={(item) => <SeasonRow season={item}></SeasonRow>}></DateScrollView>
                                    <LeagueTable league={league} statTypes={statTypes}></LeagueTable>
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path="details"
                        element={
                            <>
                                <LeagueDetails league={league} isBusy={isUpdating} onChange={onChange}></LeagueDetails>
                                <br />

                                {hasWritePermission && (
                                    <Box gap="small" direction="row">
                                        {league.archived && <Button onClick={() => startArchive()} label="Unarchive" color="status-ok"></Button>}
                                        {!league.archived && <Button onClick={() => startArchive()} label="Archive" color="status-warning"></Button>}
                                        <Button primary onClick={update} disabled={isUpdating} label="Update"></Button>
                                    </Box>
                                )}

                                <StyledModal isOpen={showArchiveModal} setIsOpen={setShowArchiveModal}>
                                    <Box width={"medium"}>
                                        {league.archived && <Text>Are you sure you want to unarchive this league?</Text>}
                                        {!league.archived && <Text>Are you sure you want to archive this league?</Text>}
                                        <Box direction="row" justify="end" pad={"small"} gap="small">
                                            <Button onClick={() => setShowArchiveModal(false)} label="Cancel"></Button>
                                            {league.archived && <Button primary onClick={() => toggleArchive()} label="Unarchive" color="status-ok"></Button>}
                                            {!league.archived && <Button primary onClick={() => toggleArchive()} label="Archive" color="status-warning"></Button>}
                                        </Box>
                                    </Box>
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path="rules"
                        element={
                            <>
                                <Box fill={true}>
                                    <FormField label="Support Markdown">
                                        <CheckBox checked={rulesAsMarkdown} label="Markdown" onChange={(e) => setRulesAsMarkdown(e.target.checked)} disabled={isUpdating} />
                                    </FormField>
                                    <FormField label="Rules">
                                        <AutoSizeTextArea value={rules || ""} onChange={(e) => setRules(e.target.value)} disabled={isUpdating} />
                                    </FormField>
                                </Box>
                            </>
                        }
                    />
                    <Route
                        path="public"
                        element={
                            <Box gap="small">
                                {/* <Button href={"/public/leagues/" + league.id} icon={idBadge} label="View Public Page"></Button> */}

                                <Box>
                                    {/* <FormField label="Dark Mode">
                                        <CheckBox checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Use Team Color">
                                        <CheckBox checked={useTeamColor} onChange={(e) => setUseTeamColor(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Reverse">
                                        <CheckBox checked={reverse} onChange={(e) => setReverse(e.target.checked)} />
                                    </FormField>

                                    <FormField label="Next Match Number">
                                        <TextInput type="number" value={matchNumber} onChange={(e) => setMatchNumber(+e.target.value)} />
                                    </FormField> */}

                                    {/* <FormField label="Reverse">
                                        <ColorPicker style={{ backgroundColor: backgroundColor }}>
                                            <input type="color" value={color} onChange={(e) => selectColor(e.target.value)} />
                                        </ColorPicker>
                                    </FormField> */}
                                </Box>

                                {/* <Button onClick={copyLink("/public/leagues/" + league.id)} icon={chain} label="Bracket"></Button>

                                <Button onClick={copyLink("/public/leagues/" + league.id + "/currentMatch")} icon={chain} label="Current Match"></Button>

                                <Button onClick={copyLink("/public/leagues/" + league.id + "/currentMatch/0/name")} icon={chain} label="Current Match Team One Name"></Button>
                                <Button onClick={copyLink("/public/leagues/" + league.id + "/currentMatch/0/logo")} icon={chain} label="Current Match Team One Logo"></Button>
                                <Button
                                    onClick={copyLink("/public/leagues/" + league.id + "/currentMatch/0/nameWithLogo")}
                                    icon={chain}
                                    label="Current Match Team One Name and Logo"
                                ></Button>

                                <Button onClick={copyLink("/public/leagues/" + league.id + "/currentMatch/1/name")} icon={chain} label="Current Match Team Two Name"></Button>
                                <Button onClick={copyLink("/public/leagues/" + league.id + "/currentMatch/1/logo")} icon={chain} label="Current Match Team Two Logo"></Button>
                                <Button
                                    onClick={copyLink("/public/leagues/" + league.id + "/currentMatch/1/nameWithLogo")}
                                    icon={chain}
                                    label="Current Match Team Two Name and Logo"
                                ></Button>

                                <Button onClick={copyLink("/public/leagues/" + league.id + "/winner")} icon={chain} label="Winner"></Button>
                                <Button onClick={copyLink("/public/leagues/" + league.id + "/winner/name")} icon={chain} label="Winner Name"></Button>
                                <Button onClick={copyLink("/public/leagues/" + league.id + "/winner/logo")} icon={chain} label="Winner Logo"></Button>
                                <Button onClick={copyLink("/public/leagues/" + league.id + "/winner/nameWithLogo")} icon={chain} label="Winner Name and Logo"></Button> */}
                            </Box>
                        }
                    />
                    <Route
                        path="error"
                        element={
                            <>
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </>
                        }
                    />
                </Routes>
            </SkeletonWrapper>
        </Box>
    );
};

export default LeagueProfile;
